import axios from "../../AxiosConfig";
import {
  AccountModel,
  Accounts,
  AddOnEligibleProductRef,
  AddOnsMerchantListResponseDto,
  AddOnsUpsertRequest,
  AddOnsUpsertResponseDto,
  AddOnProduct,
  APIKeyModel,
  APIKeysModel,
  BranchesModel,
  CreateAccountModel,
  CreateWebhookModel,
  GeneratePhoneVerificationMFARequest,
  GeneratePhoneVerificationMFAResponse,
  InviteUserModel,
  InvoiceTempalateModel,
  PortalBrandingModel,
  ProductSwitchGroup,
  ProductSwitchGroupsResponse,
  QuantityChangeGroup,
  QuantityChangeGroupRequest,
  QuantityChangeGroupsResponse,
  UpdateInvoiceTemplateConfigModel,
  RefundPolicyModel,
  SettingsCreditCard,
  SettingsInstallments,
  SettingsPaymentOptions,
  SubmitPhoneVerificationMFARequest,
  SubmitPhoneVerificationMFAResponse,
  SupportModel,
  UpdateAccountModel,
  UpdatePasswordModel,
  UpdateRiskyInvoicesSettings,
  UserModel,
  UsersModel,
  WebhooksModel,
  WebhookDeliveriesModel,
  WebhookDeliveryModel,
  WebhookDeliveryFilterParams,
  InstallmentRuleCreateDto,
  InstallmentRuleListParams,
  InstallmentPreviewDto,
  InstallmentPreviewParams,
  InstallmentRuleResponseDto,
  InstallmentRuleUpdateDto,
  DiscountRuleCreateDto,
  DiscountRuleResponseDto,
  DiscountRuleUpdateDto,
  ActivatePgwProviderRequest,
  ActivatePgwProviderResponse,
  PgwProviderListResponse,
  AccountingProviderListResponse,
  ProviderMetadataResponse,
  AccountingFieldValue,
  AccountingOrganizationAuthResponse,
  AccountingOrganizationConfigResponse,
  StartOAuthResponse,
  ResolveFieldValuesResponse,
  AccountingBranchOptionsResponse,
  AccountingBranchMappingItem,
  AccountingBranchMappingListResponse,
  PostingBehaviorResponse,
  UpdatePostingBehaviorRequest,
  OrganizationAddressResponse,
  OrganizationAddressUpdateRequest,
  ZatcaOnboardRequest,
  ZatcaOnboardStartResponse,
  ZatcaEligibilityResponse,
  ZatcaOnboardStatusResponse,
  ZatcaRenewRequest,
  ZatcaRenewStartResponse,
} from "./_models";
import { OrganizationResponse, UserResponse } from "../../../modules/auth";
import { CouponModel } from "../../coupons/core/_models";
import type { PendingInviteResponse } from "./authorizationModels";
import {
  deleteMember,
  inviteMember,
  listMembers,
  listPendingInvites,
} from "./authorizationRequests";

const API_URL = import.meta.env.VITE_APP_API_URL;
const ACCOUNTS_URL = `${API_URL}/accounts`;
const PASSWORD_URL = `${API_URL}/user/change-password`;
const ORG_URL = `${API_URL}/organization`;
const SUPPORT_URL = `${API_URL}/support`;
const INVOCIE_TEMPLATE = `${API_URL}/invoice-template`;
const BRANCH_URL = `${API_URL}/branch`;
const USER_URL = `${API_URL}/organization/users`;
const ORG_SETTINGS_URL = `${API_URL}/organization-settings`;
const WEBHOOK_URL = `${API_URL}/webhooks`;
const WEBHOOK_DELIVERIES_URL = `${API_URL}/webhook-deliveries`;
const API_KEY_URL = `${API_URL}/api-keys`;
const CHANGE_PHONE_URL = `${API_URL}/user/change-phone`;
const VERIFY_PHONE_CHANGE_URL = `${API_URL}/user/verify-phone-change`;

export function getInvoiceTemplate() {
  return axios.get<InvoiceTempalateModel>(INVOCIE_TEMPLATE);
}

export function getAccounts() {
  return axios.get<Accounts>(ACCOUNTS_URL);
}

export function getAccountById(id: string) {
  return axios.get<AccountModel>(`${ACCOUNTS_URL}/${id}`);
}

export function updateAccount(data: UpdateAccountModel) {
  return axios.put<AccountModel>(ACCOUNTS_URL + "/" + data.id, data);
}

export function createAccount(data: CreateAccountModel) {
  return axios.post<AccountModel>(ACCOUNTS_URL, data);
}

export function updatePassword(data: UpdatePasswordModel) {
  return axios.post(PASSWORD_URL, data);
}

export function contactSupport(data: SupportModel) {
  return axios.post(SUPPORT_URL, data);
}

export async function getUsers() {
  const response = await listMembers();
  const mapped: UsersModel = {
    data: response.data.data.map((member) => {
      const bindings = member.bindings.map((binding) => ({
        id: binding.id,
        role_id: binding.role.id,
        role_name: binding.role.name,
        branch_id: binding.branch?.id ?? null,
        branch_name: binding.branch?.name ?? null,
      }));
      const branchMap = new Map<string, string>();
      for (const binding of bindings) {
        if (binding.branch_id && binding.branch_name) {
          branchMap.set(binding.branch_id, binding.branch_name);
        }
      }
      return {
        id: member.id,
        first_name: member.first_name,
        last_name: member.last_name,
        en_first_name: member.first_name,
        en_last_name: member.last_name,
        email: member.email,
        created_at: member.created_at ?? undefined,
        last_login_at: member.last_login_at ?? null,
        bindings,
        // Parallel to bindings for legacy consumers; branches stay unique for edit UI.
        roles: bindings.map((binding) => ({
          role_id: binding.role_id,
          role_name: binding.role_name,
        })),
        branches: Array.from(branchMap.entries()).map(
          ([branch_id, branch_name]) => ({ branch_id, branch_name })
        ),
      };
    }),
  };
  return { ...response, data: mapped };
}

export function mapPendingInvite(invite: PendingInviteResponse): UserModel {
  const bindings = invite.bindings.map((binding) => ({
    id: binding.id,
    role_id: binding.role.id,
    role_name: binding.role.system_key ?? binding.role.name,
    branch_id: binding.branch?.id ?? null,
    branch_name: binding.branch?.name ?? null,
  }));
  const branchMap = new Map<string, string>();
  for (const binding of bindings) {
    if (binding.branch_id && binding.branch_name) {
      branchMap.set(binding.branch_id, binding.branch_name);
    }
  }
  return {
    id: invite.id,
    email: invite.email,
    created_at: invite.created_at,
    bindings,
    roles: bindings.map((binding) => ({
      role_id: binding.role_id,
      role_name: binding.role_name,
    })),
    branches: Array.from(branchMap.entries()).map(
      ([branch_id, branch_name]) => ({ branch_id, branch_name })
    ),
  };
}

export async function getPendingInvites() {
  const response = await listPendingInvites();
  return {
    ...response,
    data: { data: response.data.data.map(mapPendingInvite) },
  };
}

export function inviteUser(data: InviteUserModel) {
  if (data.bindings?.length) {
    return inviteMember({
      email: data.email,
      bindings: data.bindings,
    });
  }
  // Legacy shape — map role + branches into bindings when callers still send it.
  const roleId = data.role;
  const branchIds = data.branches ?? [];
  const bindings =
    roleId && branchIds.length > 0
      ? branchIds.map((branch_id) => ({ role_id: roleId, branch_id }))
      : roleId
        ? [{ role_id: roleId, branch_id: null }]
        : [];
  return inviteMember({ email: data.email, bindings });
}

export function updateUser(data: UserModel) {
  return axios.put(USER_URL + "/" + data.id, data);
}

export function deleteUser(id: string) {
  return deleteMember(id);
}

export function getBranches() {
  return axios.get<BranchesModel>(BRANCH_URL);
}

export function createNewBranch(data: { name: string }) {
  return axios.post(BRANCH_URL, data);
}

export function updateBranch(data: { id: string; name: string }) {
  return axios.put(BRANCH_URL + "/" + data.id, data);
}

export function deleteBranch(id: string) {
  return axios.delete(BRANCH_URL + "/" + id);
}

export function uploadInvoiceHeader(file: File) {
  const formData = new FormData();
  formData.append("file", file);

  return axios.put<InvoiceTempalateModel>(
    `${INVOCIE_TEMPLATE}/header-image`,
    formData
  );
}

export function uploadInvoiceTemaplate(
  input: UpdateInvoiceTemplateConfigModel
) {
  return axios.put<InvoiceTempalateModel>(INVOCIE_TEMPLATE, input);
}

export function uploadLogo(file: File) {
  const formData = new FormData();
  formData.append("file", file);

  return axios.put<OrganizationResponse>(ORG_URL + "/logo_upload", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
      // This is typically not needed as Axios and browsers will set it appropriately
    },
  });
}

export function uploadAvatar(file: File) {
  const formData = new FormData();
  formData.append("file", file);

  return axios.put<UserResponse>(USER_URL + "/avatar_upload", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
      // This is typically not needed as Axios and browsers will set it appropriately
    },
  });
}

export function requestPhoneChange(data: { new_phone: string }) {
  return axios.post(CHANGE_PHONE_URL, data);
}

export function verifyPhoneChange(data: { otp: string }) {
  return axios.post(VERIFY_PHONE_CHANGE_URL, data);
}

export function updatePaymentOptions(data: SettingsPaymentOptions) {
  return axios.put(`${ORG_SETTINGS_URL}/payment-options`, data);
}

export function updatePaymentLinkPaymentOptions(data: SettingsPaymentOptions) {
  return axios.put(`${ORG_SETTINGS_URL}/payment-link-payment-options`, data);
}

export function updateCreditCard(data: SettingsCreditCard) {
  return axios.put(`${ORG_SETTINGS_URL}/cards`, data);
}

export function updateInstallments(data: SettingsInstallments) {
  return axios.put(`${ORG_SETTINGS_URL}/installment`, data);
}

export function updatePaymentLinkInstallments(data: SettingsInstallments) {
  return axios.put(
    `${ORG_SETTINGS_URL}/payment-link-installment-settings`,
    data
  );
}

export function updateRiskyInvoicesSettings(data: UpdateRiskyInvoicesSettings) {
  return axios.put(`${ORG_SETTINGS_URL}/risky-invoices`, data);
}

export function getWebhooks() {
  return axios.get<WebhooksModel>(WEBHOOK_URL);
}

const WEBHOOK_V2_URL = `${API_URL}/webhooks`;

export function getWebhookEvents() {
  return axios.get<{ events: string[] } | string[]>(`${WEBHOOK_V2_URL}/events`);
}

export function createWebhook(data: CreateWebhookModel) {
  return axios.post<WebhooksModel>(WEBHOOK_URL, data);
}

export function updateWebhook(id: string, data: CreateWebhookModel) {
  return axios.put(WEBHOOK_URL + "/" + id, data);
}

export function deleteWebhook(id: string) {
  return axios.delete(WEBHOOK_URL + "/" + id);
}

export function getWebhookDeliveries(
  webhookId: string,
  params: WebhookDeliveryFilterParams = {}
) {
  const searchParams = new URLSearchParams();
  if (params.page) {
    searchParams.set("page", String(params.page));
  }
  if (params.limit) {
    searchParams.set("limit", String(params.limit));
  }
  const query = searchParams.toString();
  return axios.get<WebhookDeliveriesModel>(
    `${WEBHOOK_DELIVERIES_URL}/by-webhook/${webhookId}${query ? `?${query}` : ""}`
  );
}

export function getWebhookDeliveryById(deliveryId: string) {
  return axios.get<WebhookDeliveryModel>(
    `${WEBHOOK_DELIVERIES_URL}/${deliveryId}`
  );
}

export function replayWebhookDelivery(deliveryId: string) {
  return axios.post(`${WEBHOOK_DELIVERIES_URL}/${deliveryId}`);
}

export function getAPIKeys() {
  return axios.get<APIKeysModel>(API_KEY_URL);
}

export function createAPIKey(
  name: string,
  branch_ids?: string[],
  default_branch_id?: string,
  is_all_branches?: boolean
) {
  const payload: {
    name: string;
    branch_ids?: string[];
    default_branch_id?: string;
    is_all_branches?: boolean;
  } = {
    name,
  };

  // Only include fields if they have values
  if (branch_ids && branch_ids.length > 0) {
    payload.branch_ids = branch_ids;
  }
  if (default_branch_id) {
    payload.default_branch_id = default_branch_id;
  }
  if (is_all_branches !== undefined) {
    payload.is_all_branches = is_all_branches;
  }

  return axios.post<APIKeyModel>(API_KEY_URL, payload);
}

export function deleteAPIKey(id: string) {
  return axios.delete(API_KEY_URL + "/" + id);
}

const ORG_V2_URL = `${API_URL}/organization`;

export function updateCompanyName(data: { name?: string; name_en?: string }) {
  return axios.put(`${ORG_V2_URL}/name`, data);
}

export function getRefundPolicy() {
  return axios.get<RefundPolicyModel>(`${ORG_V2_URL}/refund-policy`);
}

export function updateRefundPolicy(data: RefundPolicyModel) {
  return axios.put<RefundPolicyModel>(`${ORG_V2_URL}/refund-policy`, data);
}

const USER_V2_URL = `${API_URL}/user`;

export function generatePhoneVerificationMFA(
  data: GeneratePhoneVerificationMFARequest
) {
  return axios.post<GeneratePhoneVerificationMFAResponse>(
    `${USER_V2_URL}/generate-phone-verification-mfa`,
    data
  );
}

export function submitPhoneVerificationMFA(
  data: SubmitPhoneVerificationMFARequest
) {
  return axios.post<SubmitPhoneVerificationMFAResponse>(
    `${USER_V2_URL}/submit-phone-verification-mfa`,
    data
  );
}

const CONSUMER_PORTAL_V2_URL = `${API_URL}/consumer_portal`;
const PORTAL_BRANDING_V2_URL = `${API_URL}/organization-settings/consumer-portal-branding`;

export function getPortalConfig(orgId: string) {
  return axios.get<PortalBrandingModel>(`${CONSUMER_PORTAL_V2_URL}/config`, {
    params: { org_id: orgId },
  });
}

export function updatePortalBranding(data: {
  button_color: string | null;
  background_color: string | null;
}) {
  return axios.put<PortalBrandingModel>(PORTAL_BRANDING_V2_URL, data);
}

const CONSUMER_PROFILE_SETTINGS_URL = `${API_URL}/organization-settings/consumer-profile`;

export interface ConsumerProfileSettings {
  product_switch?: boolean;
  subscription_cancelation?: boolean;
  add_remove_add_ons?: boolean;
  quantity_change?: boolean;
}

export function updateConsumerProfile(data: ConsumerProfileSettings) {
  return axios.put<ConsumerProfileSettings>(
    CONSUMER_PROFILE_SETTINGS_URL,
    data
  );
}

export function updateContactSettings(data: { url: string }) {
  return axios.put<{ contact?: { url?: string } }>(
    `${ORG_SETTINGS_URL}/contact`,
    data
  );
}

export function getPgwProviders() {
  return axios.get<PgwProviderListResponse>(
    `${ORG_SETTINGS_URL}/pgw-providers`
  );
}

export function activatePgwProvider(data: ActivatePgwProviderRequest) {
  return axios.post<ActivatePgwProviderResponse>(
    `${ORG_SETTINGS_URL}/pgw-providers/activate`,
    data
  );
}

const ACCOUNTING_URL = `${API_URL}/accounting`;

export function getAccountingProviders() {
  return axios.get<AccountingProviderListResponse>(ACCOUNTING_URL);
}

export function getAccountingProviderMetadata(provider: string) {
  return axios.get<ProviderMetadataResponse>(
    `${ACCOUNTING_URL}/${provider}/metadata`
  );
}

export function submitAccountingCredentials(
  provider: string,
  fields: AccountingFieldValue[]
) {
  return axios.post<AccountingOrganizationAuthResponse>(
    `${ACCOUNTING_URL}/${provider}/credentials`,
    { fields }
  );
}

export function startAccountingOAuth(
  provider: string,
  fields: AccountingFieldValue[]
) {
  return axios.post<StartOAuthResponse>(
    `${ACCOUNTING_URL}/${provider}/authorize`,
    { fields }
  );
}

export function resolveAccountingFieldValues(
  provider: string,
  knownValues: Record<string, string>,
  forceRefresh: string[] = []
) {
  return axios.post<ResolveFieldValuesResponse>(
    `${ACCOUNTING_URL}/${provider}/fields/values`,
    { known_values: knownValues, force_refresh: forceRefresh }
  );
}

export function submitAccountingConfig(
  provider: string,
  fields: AccountingFieldValue[]
) {
  return axios.post<AccountingOrganizationConfigResponse>(
    `${ACCOUNTING_URL}/${provider}/config`,
    { fields }
  );
}

/** GET config for an already-configured provider — auth fields are stripped out. */
export function getAccountingProviderConfig(provider: string) {
  return axios.get<AccountingOrganizationConfigResponse>(
    `${ACCOUNTING_URL}/${provider}/config`
  );
}

/** Edit an existing integration's config fields without touching auth/credentials. */
export function updateAccountingProviderConfig(
  provider: string,
  fields: AccountingFieldValue[]
) {
  return axios.put<AccountingOrganizationConfigResponse>(
    `${ACCOUNTING_URL}/${provider}/config`,
    { fields }
  );
}

export function activateAccountingProvider(provider: string) {
  return axios.post<AccountingOrganizationAuthResponse>(
    `${ACCOUNTING_URL}/${provider}/activate`
  );
}

export function deactivateAccountingProvider(provider: string) {
  return axios.post<AccountingOrganizationAuthResponse>(
    `${ACCOUNTING_URL}/${provider}/deactivate`
  );
}

export function getAccountingBranchOptions(provider: string) {
  return axios.get<AccountingBranchOptionsResponse>(
    `${ACCOUNTING_URL}/${provider}/branches/options`
  );
}

export function getAccountingBranchMappings(provider: string) {
  return axios.get<AccountingBranchMappingListResponse>(
    `${ACCOUNTING_URL}/${provider}/branches`
  );
}

export function updateAccountingBranchMappings(
  provider: string,
  mappings: AccountingBranchMappingItem[]
) {
  return axios.put<AccountingBranchMappingListResponse>(
    `${ACCOUNTING_URL}/${provider}/branches`,
    { mappings }
  );
}

/** Org-wide accounting posting settings (not scoped to a provider). */
export function getOrganizationPostingBehavior() {
  return axios.get<PostingBehaviorResponse>(`${ORG_V2_URL}/posting-behavior`);
}

/** Only the fields sent are updated; `sync_start_date: null` clears the date. */
export function updateOrganizationPostingBehavior(
  request: UpdatePostingBehaviorRequest
) {
  return axios.put<PostingBehaviorResponse>(
    `${ORG_V2_URL}/posting-behavior`,
    request
  );
}

/**
 * PUT /api/v2/organization-settings/consumer-portal-branding/favicon
 * Multipart field name must be `file` (FastAPI UploadFile).
 * @see https://dev-docs.streampay.sa/api/update-customer-portal-branding-favicon-api-v-2-organization-settings-consumer-portal-branding-favicon-put
 */
export function uploadPortalFavicon(file: File) {
  const fd = new FormData();
  fd.append("file", file);
  return axios.put<PortalBrandingModel>(
    `${PORTAL_BRANDING_V2_URL}/favicon`,
    fd,
    {
      transformRequest: [
        (data, headers) => {
          if (data instanceof FormData && headers) {
            const h = headers as { delete?: (key: string) => void };
            if (typeof h.delete === "function") {
              h.delete("Content-Type");
            }
          }
          return data;
        },
      ],
    }
  );
}

const ADD_ONS_URL = `${API_URL}/add-ons`;

export function getAddOns() {
  return axios.get<AddOnsMerchantListResponseDto>(ADD_ONS_URL);
}

/** Normalizes GET /v2/add-ons response for UI + sync. */
export function parseAddOnsListResponse(data: unknown): {
  eligibilityMap: Record<string, AddOnEligibleProductRef[]>;
  addOnProductIds: string[];
} {
  if (data == null) {
    return { eligibilityMap: {}, addOnProductIds: [] };
  }
  if (Array.isArray(data)) {
    const ids = (data as AddOnProduct[])
      .map((p) => p.id)
      .filter((id): id is string => typeof id === "string" && Boolean(id));
    return { eligibilityMap: {}, addOnProductIds: ids };
  }
  if (typeof data === "object" && data !== null && "add_ons" in data) {
    const entries = (data as AddOnsMerchantListResponseDto).add_ons ?? [];
    const eligibilityMap: Record<string, AddOnEligibleProductRef[]> = {};
    const addOnProductIds: string[] = [];
    for (const entry of entries) {
      const addOnId = entry.add_on_product?.id;
      if (!addOnId) continue;
      addOnProductIds.push(addOnId);
      eligibilityMap[addOnId] = (entry.eligible_products ?? [])
        .filter((p) => typeof p.id === "string" && Boolean(p.id))
        .map((p) => ({ id: p.id }));
    }
    return { eligibilityMap, addOnProductIds };
  }
  return { eligibilityMap: {}, addOnProductIds: [] };
}

/** PUT /v2/add-ons — full replace of eligible_product_ids for one add_on_product_id. */
export function putAddOnEligibility(payload: AddOnsUpsertRequest) {
  return axios.put<AddOnsUpsertResponseDto>(ADD_ONS_URL, payload);
}

const PRODUCT_SWITCH_GROUPS_URL = `${API_URL}/product-switch-groups`;

export function getProductSwitchGroups() {
  return axios.get<ProductSwitchGroupsResponse>(PRODUCT_SWITCH_GROUPS_URL);
}

/** Normalizes GET-by-id or nested `{ data: group }` payloads to a `ProductSwitchGroup`. */
export function parseProductSwitchGroupPayload(
  data: unknown
): ProductSwitchGroup {
  if (data == null || typeof data !== "object") {
    throw new Error("Invalid product switch group response");
  }
  const obj = data as Record<string, unknown>;
  if ("data" in obj && obj.data != null && typeof obj.data === "object") {
    return parseProductSwitchGroupPayload(obj.data);
  }
  if (typeof obj.id !== "string") {
    throw new Error("Invalid product switch group response");
  }
  return data as ProductSwitchGroup;
}

export function getProductSwitchGroup(groupId: string) {
  return axios.get<ProductSwitchGroup | { data: ProductSwitchGroup }>(
    `${PRODUCT_SWITCH_GROUPS_URL}/${groupId}`
  );
}

export async function fetchProductSwitchGroupDetail(
  groupId: string
): Promise<ProductSwitchGroup> {
  const res = await getProductSwitchGroup(groupId);
  return parseProductSwitchGroupPayload(res.data);
}

export function createProductSwitchGroup(payload: {
  name: string;
  product_ids: string[];
}) {
  return axios.post<ProductSwitchGroup>(PRODUCT_SWITCH_GROUPS_URL, payload);
}

export function updateProductSwitchGroup(
  groupId: string,
  payload: { name: string; product_ids: string[] }
) {
  return axios.put<ProductSwitchGroup>(
    `${PRODUCT_SWITCH_GROUPS_URL}/${groupId}`,
    payload
  );
}

export function deleteProductSwitchGroup(groupId: string) {
  return axios.delete(`${PRODUCT_SWITCH_GROUPS_URL}/${groupId}`);
}

const QUANTITY_CHANGES_URL = `${API_URL}/quantity-changes`;

export function getQuantityChangeGroups() {
  return axios.get<QuantityChangeGroupsResponse>(QUANTITY_CHANGES_URL);
}

export function createQuantityChangeGroup(payload: QuantityChangeGroupRequest) {
  return axios.post<QuantityChangeGroup>(QUANTITY_CHANGES_URL, payload);
}

export function updateQuantityChangeGroup(
  groupId: string,
  payload: QuantityChangeGroupRequest
) {
  return axios.put<QuantityChangeGroup>(
    `${QUANTITY_CHANGES_URL}/${groupId}`,
    payload
  );
}

export function deleteQuantityChangeGroup(groupId: string) {
  return axios.delete(`${QUANTITY_CHANGES_URL}/${groupId}`);
}

const INSTALLMENT_RULES_URL = `${ORG_SETTINGS_URL}/installment-rules`;

export function getInstallmentRules(params?: InstallmentRuleListParams) {
  return axios.get<InstallmentRuleResponseDto[]>(INSTALLMENT_RULES_URL, {
    params,
  });
}

export function getInstallmentRule(ruleId: string) {
  return axios.get<InstallmentRuleResponseDto>(
    `${INSTALLMENT_RULES_URL}/${ruleId}`
  );
}

export function createInstallmentRule(data: InstallmentRuleCreateDto) {
  return axios.post<InstallmentRuleResponseDto>(INSTALLMENT_RULES_URL, data);
}

export function updateInstallmentRule(
  ruleId: string,
  data: InstallmentRuleUpdateDto
) {
  return axios.put<InstallmentRuleResponseDto>(
    `${INSTALLMENT_RULES_URL}/${ruleId}`,
    data
  );
}

export function deleteInstallmentRule(ruleId: string) {
  return axios.delete(`${INSTALLMENT_RULES_URL}/${ruleId}`);
}

export function getOverrideInstallmentRules(
  params: Pick<InstallmentRuleListParams, "installment_context_type"> = {}
) {
  return axios.get<InstallmentRuleResponseDto[]>(
    `${INSTALLMENT_RULES_URL}/override`,
    { params }
  );
}

export function getOrganizationAddress() {
  return axios.get<OrganizationAddressResponse>(`${ORG_SETTINGS_URL}/address`);
}

export function updateOrganizationAddress(
  data: OrganizationAddressUpdateRequest
) {
  return axios.put<OrganizationAddressResponse>(
    `${ORG_SETTINGS_URL}/address`,
    data
  );
}

const ZATCA_URL = `${API_URL}/zatca`;

export function startZatcaOnboarding(data: ZatcaOnboardRequest) {
  return axios.post<ZatcaOnboardStartResponse>(`${ZATCA_URL}/onboard`, data);
}

export function getZatcaOnboardingStatus() {
  return axios.get<ZatcaOnboardStatusResponse>(`${ZATCA_URL}/onboard/status`);
}

export function getZatcaEligibility() {
  return axios.get<ZatcaEligibilityResponse>(`${ZATCA_URL}/eligibility`);
}

export function renewZatcaProductionCsid(data: ZatcaRenewRequest) {
  return axios.post<ZatcaRenewStartResponse>(`${ZATCA_URL}/renew`, data);
}

export function getInstallmentPreview(params: InstallmentPreviewParams) {
  const { override_rule_id, plan_context_type, ...rest } = params;
  return axios.get<InstallmentPreviewDto>(`${INSTALLMENT_RULES_URL}/preview`, {
    params: {
      ...rest,
      installment_context_type: plan_context_type,
      ...(override_rule_id ? { override_rule_id } : {}),
    },
  });
}

const DISCOUNT_RULES_URL = `${ORG_SETTINGS_URL}/discount-rules`;

export function getDiscountRules() {
  return axios.get<DiscountRuleResponseDto[]>(DISCOUNT_RULES_URL);
}

export function getDiscountRule(ruleId: string) {
  return axios.get<DiscountRuleResponseDto>(`${DISCOUNT_RULES_URL}/${ruleId}`);
}

export function createDiscountRule(data: DiscountRuleCreateDto) {
  return axios.post<DiscountRuleResponseDto>(DISCOUNT_RULES_URL, data);
}

export function updateDiscountRule(
  ruleId: string,
  data: DiscountRuleUpdateDto
) {
  return axios.put<DiscountRuleResponseDto>(
    `${DISCOUNT_RULES_URL}/${ruleId}`,
    data
  );
}

export function deleteDiscountRule(ruleId: string) {
  return axios.delete(`${DISCOUNT_RULES_URL}/${ruleId}`);
}

export interface PreviewAutomaticDiscountParams {
  beneficiary_id: string;
  product_id: string;
  currency: string;
}

export function previewAutomaticDiscount(
  params: PreviewAutomaticDiscountParams
) {
  return axios.get<CouponModel | null>(
    `${DISCOUNT_RULES_URL}/automatic-discount`,
    { params }
  );
}
